import { Component, computed, inject } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { SidebarService } from '../../services/sidebar.service';
import { SidebarPanelType } from '../../models/sidebar-panel';
import { BillingSearchResult, ReportService } from '../../../features/reports/services/report.service';
import { getBillingTypeLabel } from '../../constants/domain.constants';
import { LoadingOverlayService } from '../../services/loading-overlay.service';

@Component({
  selector: 'app-invoice-detail-panel',
  imports: [CurrencyPipe, DatePipe],
  templateUrl: './invoice-detail-panel.component.html',
  styleUrl: './invoice-detail-panel.component.scss'
})
export class InvoiceDetailPanelComponent {
  private readonly sidebarService = inject(SidebarService);
  private readonly reportService = inject(ReportService);
  private readonly loadingOverlay = inject(LoadingOverlayService);

  readonly invoice = computed(() => {
    const panel = this.sidebarService.currentPanel();
    if (panel?.type === SidebarPanelType.InvoiceDetail) {
      return panel.data;
    }
    return null;
  });

  readonly billingTypeLabel = computed(() => {
    const i = this.invoice();
    return i?.billingType != null ? getBillingTypeLabel(i.billingType) : '-';
  });

  readonly canDownloadPdf = computed(() => {
    const bt = this.invoice()?.billingType;
    // Vendor Invoice (1) has no PDF report in the legacy implementation.
    return bt != null && bt !== 1;
  });

  downloadInvoice(): void {
    const i = this.invoice();
    if (!i || i.billingType == null) return;

    this.loadingOverlay.show('Getting invoice. Please do not refresh.');
    this.reportService.getBillingInvoice(i.id, i.billingType).subscribe({
      next: (blob) => {
        this.loadingOverlay.hide();
        const safeRef = i.refNumber ? i.refNumber.replace(/[^a-zA-Z0-9_-]/g, '_') : i.id;
        this.downloadBlob(blob, `billing-invoice-${safeRef}.pdf`);
      },
      error: () => this.loadingOverlay.hide()
    });
  }

  downloadDetails(): void {
    const i = this.invoice();
    if (!i || i.billingType == null) return;

    this.loadingOverlay.show('Getting invoice details. Please do not refresh.');
    this.reportService.getBillingHistoryExcel(i.id, i.billingType).subscribe({
      next: (rows) => {
        this.loadingOverlay.hide();
        if (!rows?.length) return;

        const headers = [
          'Invoice Number',
          'Order Number',
          'Invoiced Date',
          'Fiscal Period',
          'Account',
          'Address',
          'City',
          'State',
          'Zip',
          'Service Provider',
          'Service Date',
          'Product',
          'Description',
          'Unit Price',
          'Quantity',
          'Total',
          'Vendor Invoice Number',
          'Customer Reference Number',
          'Consolidated Recap Number'
        ];

        const dataRows = rows.map((r) => [
          r.invoiceNumber ?? '',
          r.orderNumber ?? '',
          r.invoiceDate ? new Date(r.invoiceDate).toLocaleDateString() : '',
          r.invoiceFiscalPeriod ?? '',
          r.account ?? '',
          r.accountAddressLine1 ?? '',
          r.accountAddressCity ?? '',
          r.accountAddressState ?? '',
          r.accountAddressZip ?? '',
          r.serviceProvider ?? '',
          r.serviceDate ? new Date(r.serviceDate).toLocaleDateString() : '',
          r.product ?? '',
          r.productDescription ?? '',
          r.unitPrice != null ? String(r.unitPrice) : '',
          r.quantity != null ? String(r.quantity) : '',
          r.lineTotal != null ? String(r.lineTotal) : '',
          r.vendorInvoiceNumber ?? '',
          r.customerReferenceNumber ?? '',
          r.consolidatedRecapNumber ?? ''
        ]);

        const csv = [headers, ...dataRows]
          .map((row) => row.map((c) => this.escapeCsv(c)).join(','))
          .join('\r\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const safeRef = i.refNumber ? i.refNumber.replace(/[^a-zA-Z0-9_-]/g, '_') : i.id;
        this.downloadBlob(blob, `billing-history-${safeRef}.csv`);
      },
      error: () => this.loadingOverlay.hide()
    });
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const nav = navigator as Navigator & { msSaveBlob?: (blob: Blob, fileName: string) => boolean };
    if (nav.msSaveBlob) {
      nav.msSaveBlob(blob, filename);
      return;
    }
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', filename);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private escapeCsv(value: unknown): string {
    const str = value == null ? '' : String(value);
    if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  }
}
